import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'
import ReactSelect from 'react-select'
import debounce from 'debounce-promise'
import { isEqual, get } from 'lodash'
import { Field } from 'redux-form'
import { FieldError } from 'components/misc'
import { MaterialLoader } from 'components/loaders'
import fieldId from 'constants/layout/fieldId'
import styles from './select.styl'
import config from 'config'

@injectIntl
class Select extends Component {
  static propTypes = {
    intl: PropTypes.object.isRequired,
    updater: PropTypes.any,
    options: PropTypes.array,
    autocomplete: PropTypes.bool,
    disableRedux: PropTypes.bool,
    searchable: PropTypes.bool,
    withIcon: PropTypes.bool,
    withSvg: PropTypes.bool,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,
    clearable: PropTypes.bool,
    labelless: PropTypes.bool,
    onSelectChange: PropTypes.func,
    onInputChange: PropTypes.func,
    promise: PropTypes.func,
    intlKeyPlaceholder: PropTypes.string,
    autoloadValue: PropTypes.string,
    placeholder: PropTypes.string,
    intlKey: PropTypes.string,
    label: PropTypes.string.isRequired,
    multi: PropTypes.bool,
    name: PropTypes.string.isRequired,
    subLabelAsync: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    validate: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.arrayOf(PropTypes.func)
    ])
  }

  static defaultProps = {
    validate: () => {},
    subLabelAsync: false,
    disableRedux: false,
    loading: false,
    searchable: false,
    clearable: true,
    multi: false,
    placeholder: null,
    intlKeyPlaceholder: 'label.select',
    autoloadValue: null
  }

  constructor(props) {
    super(props)
    this.getOptions = debounce(this.getOptions, 500)
  }

  shouldComponentUpdate(nextProps) {
    const { disabled, updater, autocomplete, params, intl } = this.props

    if (
      disabled !== nextProps.disabled ||
      updater !== nextProps.updater ||
      intl.locale !== nextProps.intl.locale
    ) {
      return true
    }

    if (autocomplete) {
      // discuss this and find a better solution
      return !isEqual(params, nextProps.params)
    }

    return true
  }

  getOptions = input => {
    const { autoloadValue } = this.props

    if (!this.props.promise) {
      console.warn('Promise must be specified ')
      return false
    }
    return this.props
      .promise({
        name: input || autoloadValue,
        ...this.props.params
      })
      .then(({ body }) => ({
        options: body.map(item => ({
          label: item.name,
          subLabel: this.getSubLabel(item),
          value: item.id
        }))
      }))
  }

  getSubLabel = item => {
    let subLabel = null

    if (this.props.subLabelAsync) {
      const { subLabelAsync: { keys, splitters } } = this.props

      subLabel = ''
      for (const key in keys) {
        if (keys.hasOwnProperty(key)) {
          if (!get(item, keys[key])) {
            // this.hasAsyncLabel = false
            return
          }

          subLabel += get(item, keys[key])

          if (splitters[key]) {
            subLabel += splitters[key]

            this.hasAsyncLabel = true
          }
        }
      }
    }

    return subLabel
  }

  hasAsyncLabel = true

  handleChange = (field, obj) => {
    this.props.onSelectChange && this.props.onSelectChange(obj || {})

    if (!this.props.disableRedux) {
      const data = this.props.multi ? obj : get(obj, 'value')

      field.input.onChange(data)
    }
  }

  handleInputChange = (field, obj) => {
    if (this.props.onInputChange) {
      this.props.onInputChange(obj.value)
    }
  }

  handleRenderOption = option => (
    <span className={styles.option}>
      {this.props.withIcon &&
        <span className={`ico ${styles.icoSelect} ico--${option.ico}`} />}
      {this.props.withSvg &&
        <img
          className={styles.icoSelect}
          src={`${config.publicPath}/flags/${option.ico}.svg`}
        />}
      <span className="select-value-label">{option.label}</span>
    </span>
  )

  handleRenderValue = option => (
    <span>
      {this.props.withIcon &&
        <span className={`ico ${styles.icoSelect} ico--${option.ico}`} />}
      {this.props.withSvg &&
        <img
          className={styles.icoSelect}
          src={`${config.publicPath}/flags/${option.ico}.svg`}
        />}
      <span className="select-value-label">{option.label}</span>
    </span>
  )

  renderValue = option => (
    <div>
      {option.label}
      {option.subLabel
        ? <span className={styles.subLabel}>{option.subLabel}</span>
        : <span className={styles.subLabel}>---</span>}
    </div>
  )

  renderOption = option => (
    <div>
      {option.label}
      {option.subLabel
        ? <span className={styles.subLabel}>{option.subLabel}</span>
        : <span className={styles.subLabel}>---</span>}
    </div>
  )

  renderField = field => {
    const {
      name,
      options,
      autocomplete,
      autoloadValue,
      disabled,
      loading,
      searchable,
      clearable,
      placeholder,
      subLabelAsync,
      intl,
      intlKeyPlaceholder,
      multi
    } = this.props
    const { input, meta } = field

    const isError = meta.touched && meta.error
    const classNames = ['controls', styles.controls]

    isError && classNames.push(styles.error)
    disabled && classNames.push(styles.disabled)
    subLabelAsync &&
      this.hasAsyncLabel &&
      input.value &&
      classNames.push(styles.large)

    return (
      <div className={classNames.join(' ')}>
        {autocomplete &&
          <ReactSelect.Async
            {...this.props}
            matchProp="label"
            value={input.value}
            onChange={val => this.handleChange(field, val)}
            className="form-control--select"
            loadOptions={this.getOptions}
            options={[]}
            autoload={Boolean(autoloadValue)}
            searchable
            cache={false}
            id={name}
            loadingPlaceholder={intl.formatMessage({
              id: 'global.loading',
              defaultMessage: 'Loading'
            })}
            noResultsText={intl.formatMessage({
              id: 'label.no_results',
              defaultMessage: 'No results'
            })}
            placeholder={
              intlKeyPlaceholder
                ? intl.formatMessage({
                    id: intlKeyPlaceholder,
                    defaultMessage: placeholder
                  })
                : intl.formatMessage({ id: 'label.select_async' })
            }
            optionRenderer={subLabelAsync ? this.renderOption : null}
            valueRenderer={subLabelAsync ? this.renderValue : null}
          />}
        {!autocomplete &&
          <ReactSelect
            {...this.props}
            matchProp="label"
            value={input.value}
            onChange={val => this.handleChange(field, val)}
            onInputChange={val => this.handleInputChange(field, val)}
            className="form-control--select"
            options={options}
            searchable={searchable}
            multi={multi}
            joinValues={multi}
            loadingPlaceholder={intl.formatMessage({
              id: 'global.loading',
              defaultMessage: 'Loading'
            })}
            noResultsText={intl.formatMessage({
              id: 'label.no_results',
              defaultMessage: 'No results'
            })}
            optionRenderer={this.handleRenderOption}
            valueRenderer={this.handleRenderValue}
            id={name}
            disabled={disabled || loading}
            clearable={false}
            placeholder={
              placeholder
                ? intl.formatMessage({
                    id: intlKeyPlaceholder,
                    defaultMessage: placeholder
                  })
                : intl.formatMessage({ id: 'label.select' })
            }
          />}
        {Boolean(input.value) &&
          clearable &&
          <button
            type="button"
            className={styles.btnClear}
            onClick={() => this.handleChange(field, { value: null })}
          >
            <span className={`ico ico--close ${styles.ico}`} />
          </button>}
        <FieldError toShow={isError} message={meta.error} />
      </div>
    )
  }

  render() {
    const { label, name, labelless, intlKey, disabled, loading } = this.props
    const classNames = ['control-label', styles.controlLabel]

    labelless && classNames.push('sr-only')
    disabled && classNames.push('control-label-disabled')

    return (
      <div className="form-group" id={`${fieldId}.${name}`}>
        <label htmlFor={name} className={classNames.join(' ')}>
          {intlKey
            ? <FormattedMessage id={intlKey} defaultMessage={label} />
            : label}
          {loading && !labelless
            ? <span className={styles.labelLoader}>
              <MaterialLoader tiny />
            </span>
            : null}
        </label>
        <Field
          name={name}
          component={this.renderField}
          {...this.props}
          validate={this.props.validate}
        />
      </div>
    )
  }
}

export default Select
